import { getFieldLabel } from "./printLabels";
import { formatCurrency } from "./currencyUtils";
import { formatPrintDate } from "./dateUtils";

const FIELD_KEYS = [
  "institute_name",
  "branch_name",
  "transaction_type",
  "customer_name",
  "customer_number",
  "customer_address",
  "branch_address",
  "transaction_number",
  "total_amount",
  "date",
  "total_amount_words",
  "cheque_number",
  "reprint",
  "print_time",
  "entered_by_id",
  "entered_by_code",
  "entered_by_ref",
  "transaction_type_id",
  "updated_by_name",
  "updated_by_code",
  "updated_by_id",
  "updated_by_ref",
  "approved_by_name",
  "approved_by_code",
  "approved_by_id",
  "approved_by_ref",
  "institute_address",
  "institute_reg_num",
  "institute_phone",
  "institute_email",
  "institute_reg_date",
  "branch_phone",
  "branch_email",
  "description",
  "interest_rate",
  "period",
  "interest_rate_period",
  "guarantor_members",
  "invoice_date",
  "credit_period",
  "note"
];

const cm = (v: string | number | undefined | null): string => {
  if (v === undefined || v === null || v === "") return "0cm";
  const str = String(v);
  if (str.includes("cm") || str.includes("%") || str.includes("px") || str.includes("em")) return str;
  return `${v}cm`;
};

const cmToNumber = (v: string | number | undefined | null, fallback = 0): number => {
  if (v === undefined || v === null || v === "") return fallback;
  const parsed = Number.parseFloat(String(v).replace("cm", ""));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const escapeHtml = (value: unknown): string => {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const paginateRows = <T,>(
  rows: T[],
  rowsPerPage: number,
  rowsOnLastPage: number
): T[][] => {
  if (rows.length === 0) return [[]];

  const pages: T[][] = [];
  let start = 0;
  let remaining = rows.length;

  while (remaining > rowsOnLastPage) {
    const take = Math.min(rowsPerPage, remaining - 1);
    pages.push(rows.slice(start, start + take));
    start += take;
    remaining -= take;
  }

  pages.push(rows.slice(start));
  return pages;
};

export const generatePrintHTML = (payload: any, config: any, lang: string = "en") => {
  const resolvePosition = (fieldKey: string) => {
    const field = config?.fields?.[fieldKey];

    if (field?.top !== undefined && field?.left !== undefined) {
      return {
        top: cm(field.top),
        left: cm(field.left),
      };
    }

    // Backward compatibility for old section/order format.
    const section = config?.sections?.[field?.section] || {};
    const topValue = cmToNumber(section.top, 0) + (Number(field?.order) || 0) * cmToNumber(section.line_gap, 0.8);
    const fallbackLeft = field?.align === "right"
      ? cmToNumber(config?.page?.width, 21) - cmToNumber(config?.layout?.right_margin, 2) - 5
      : cmToNumber(config?.layout?.left_margin, 2);

    return {
      top: cm(topValue),
      left: cm(fallbackLeft),
    };
  };

  const resolveLabelPosition = (fieldKey: string) => {
    const field = config?.fields?.[fieldKey];
    if (field?.label_top !== undefined && field?.label_left !== undefined) {
      return {
        top: cm(field.label_top),
        left: cm(field.label_left),
      };
    }
    return null;
  };

  const getMultilingualValue = (key: string) => {
    let isTranslate = true;
    if (config && typeof config === 'object') {
      if (config.fields && config.fields[key] && config.fields[key].translate !== undefined) {
        isTranslate = Boolean(config.fields[key].translate);
      } else if (config[key] && config[key].translate !== undefined) {
        isTranslate = Boolean(config[key].translate);
      }
    }

    const cleanKey = key.replace(/_copy_\d+$/, "");

    const cleanLang = isTranslate ? (lang.startsWith("si") ? "si" : lang.startsWith("ta") ? "ta" : "en") : "en";
    if (cleanLang === "si") {
      const siVal = payload?.[`${cleanKey}_ln2`] ?? payload?.[`${cleanKey}_si`] ?? payload?.[`${cleanKey}_sinhala`];
      if (siVal !== undefined && siVal !== null && siVal !== "") return siVal;
    } else if (cleanLang === "ta") {
      const taVal = payload?.[`${cleanKey}_ln3`] ?? payload?.[`${cleanKey}_ta`] ?? payload?.[`${cleanKey}_tamil`];
      if (taVal !== undefined && taVal !== null && taVal !== "") return taVal;
    }
    return payload?.[`${cleanKey}_ln1`] ?? payload?.[`${cleanKey}_en`] ?? payload?.[`${cleanKey}_english`] ?? payload?.[cleanKey];
  };

  const formatFontSize = (v: string | number | undefined | null): string => {
    if (v === undefined || v === null || v === "") return "12px";
    const str = String(v).trim();
    if (/^[0-9]+$/.test(str)) return `${str}px`;
    return str;
  };

  const renderField = (key: string) => {
    const field = config?.fields?.[key];
    if (!field) return "";

    const isVisible = field.visible === undefined
      ? true
      : !(field.visible === false || field.visible === 0 || field.visible === "0");

    if (!isVisible) return "";

    const cleanKey = key.replace(/_copy_\d+$/, "");

    let value = getMultilingualValue(key);
    if (cleanKey === "renew_date") {
      value = getMultilingualValue("reneve_date");
    }
    if (cleanKey === "print_time") {
      value = formatPrintDate(new Date(), config?.time_format || 'HH:mm:ss');
    }
    if (cleanKey === "open_time") {
      const openDate = payload?.open_date;
      if (openDate) {
        const d = new Date(openDate);
        if (!isNaN(d.getTime())) {
          value = d.toTimeString().split(' ')[0];
        }
      }
    }
    if ((cleanKey === "interest_rate_by_12" || cleanKey === "interest_rate/12") && payload?.interest_rate != null) {
      const rate = parseFloat(payload.interest_rate);
      if (!isNaN(rate)) {
        value = (rate / 12).toFixed(2);
      }
    }
    if (cleanKey === "total_amount" && value !== undefined && value !== null && value !== "") {
      const numVal = Number(String(value).replace(/,/g, ""));
      if (!isNaN(numVal)) {
        value = formatCurrency(numVal);
      }
    }
    const dateFields = ["institute_reg_date", "opening_date", "date_of_birth", "member_date", "last_transaction_date", "credit_limit_date", "entered_date", "date", "open_date", "reneve_date", "renew_date", "invoice_date"];
    if (dateFields.includes(cleanKey) && value) {
      const formatted = formatPrintDate(value, config?.date_format);
      if (formatted) {
        value = formatted;
      }
    }
    if (config?.hide_na && typeof value === 'string' && value.trim().toUpperCase() === 'N/A') {
      value = "";
    }
    const hasValue = value !== undefined && value !== null && value !== "";

    // Resolve typography styling values
    const fSize = formatFontSize(field?.font_size);
    const isBold = field?.bold === true || field?.bold === 1 || field?.bold === "1" || field?.bold === "true";
    const isItalic = field?.italic === true || field?.italic === 1 || field?.italic === "1" || field?.italic === "true";
    
    const weightStyle = isBold ? "bold" : "normal";
    const italicStyle = isItalic ? "italic" : "normal";

    let labelHtml = "";
    if (field?.show_label) {
      const labelPos = resolveLabelPosition(key);
      if (labelPos) {
        const labelText = getFieldLabel(key, lang, config);
        labelHtml = `
          <div style="
            position: absolute;
            top: ${labelPos.top};
            left: ${labelPos.left};
            font-size: ${fSize};
            font-weight: ${weightStyle};
            font-style: ${italicStyle};
            white-space: nowrap;
          ">
            ${escapeHtml(labelText)}
          </div>
        `;
      }
    }

    if (!hasValue) return labelHtml;

    const pos = resolvePosition(key);

    return `
      ${labelHtml}
      <div style="
        position: absolute;
        top: ${pos.top};
        left: ${pos.left};
        font-size: ${fSize};
        font-weight: ${weightStyle};
        font-style: ${italicStyle};
        white-space: nowrap;
      ">
        ${escapeHtml(value)}
      </div>
    `;
  };

  const getMultilingualAccountName = (row: any) => {
    const isTranslate = config?.sections?.table?.account_name_translate !== "false";
    const cleanLang = isTranslate ? (lang.startsWith("si") ? "si" : lang.startsWith("ta") ? "ta" : "en") : "en";
    if (cleanLang === "si") {
      const siVal = row?.account_name_ln2 ?? row?.account_name_si ?? row?.account_name_sinhala;
      if (siVal !== undefined && siVal !== null && siVal !== "") return siVal;
    } else if (cleanLang === "ta") {
      const taVal = row?.account_name_ln3 ?? row?.account_name_ta ?? row?.account_name_tamil;
      if (taVal !== undefined && taVal !== null && taVal !== "") return taVal;
    }
    return row?.account_name_ln1 ?? row?.account_name_en ?? row?.account_name_english ?? row?.account_name ?? "";
  };

  const colDefinitions: Record<string, { label: string, defaultWidth: string, defaultAlign: string }> = {
    account_number: { label: "Account No", defaultWidth: "30%", defaultAlign: "left" },
    account_name: { label: "Account Name", defaultWidth: "45%", defaultAlign: "left" },
    amount: { label: "Amount", defaultWidth: "25%", defaultAlign: "right" },
    balance: { label: "Balance", defaultWidth: "25%", defaultAlign: "right" },
    debit: { label: "Debit", defaultWidth: "25%", defaultAlign: "right" },
    credit: { label: "Credit", defaultWidth: "25%", defaultAlign: "right" },
    description: { label: "Description", defaultWidth: "40%", defaultAlign: "left" },
    cheque_num: { label: "Cheque No", defaultWidth: "25%", defaultAlign: "left" },
    unit_price: { label: "Unit Price", defaultWidth: "20%", defaultAlign: "right" },
    qty: { label: "Qty", defaultWidth: "10%", defaultAlign: "right" },
  };

  const rows = (payload?.accounts || []).map((row: any) => {
    const formattedRow: any = {};
    for (const key of Object.keys(colDefinitions)) {
      if (key === "account_name") {
        formattedRow[key] = getMultilingualAccountName(row);
      } else {
        formattedRow[key] = row?.[key] ?? "";
      }
    }
    return formattedRow;
  });

  const totalAmount = rows.reduce((sum: number, row: any) => sum + Number(row.amount || 0), 0);

  const pageWidth = cm(config?.page?.width || "21cm");
  const pageHeight = cm(config?.page?.height || "14.8cm");
  const pageHeightNum = cmToNumber(config?.page?.height, 14.8);
  const tableTopNum = cmToNumber(config?.sections?.table?.top, 7.5);

  const rowHeight = Math.max(0.4, cmToNumber(config?.sections?.table?.row_height, 0.8));

  // Keep this deterministic: no hardcoded header/footer assumptions.
  // If needed, these can be tuned per-client via config.
  const bottomPadding = cmToNumber(config?.tableMeta?.bottom_padding, 0);
  const hideHeader = config?.sections?.table?.hide_header === true || config?.sections?.table?.hide_header === "true";
  const headerHeight = hideHeader ? 0 : rowHeight;
  const hideFooter = config?.sections?.table?.hide_footer === true || config?.sections?.table?.hide_footer === "true";
  const footerHeight = hideFooter ? 0 : cmToNumber(config?.tableMeta?.footer_height, 0.8);

  const customTableHeight = config?.sections?.table?.height;
  const customTableHeightNum = customTableHeight ? cmToNumber(customTableHeight, 0) : 0;

  const usableHeight = customTableHeightNum > 0 
    ? customTableHeightNum 
    : Math.max(0, pageHeightNum - tableTopNum - bottomPadding);

  const usableHeightForRows = Math.max(0.1, usableHeight - headerHeight);

  const rowsPerPage = Math.max(1, Math.floor((usableHeightForRows + 0.0001) / rowHeight));
  const rowsOnLastPage = Math.max(
    1,
    Math.floor((Math.max(0, usableHeightForRows - footerHeight) + 0.0001) / rowHeight)
  );

  const pagedRows = paginateRows(rows, rowsPerPage, rowsOnLastPage);

  const configKeys = Object.keys(config?.fields || {});
  const allKeys = Array.from(new Set([...FIELD_KEYS, ...configKeys]));

  const getTableKeys = () => {
    if (!config?.sections) return [];
    const keys = ["table", ...Object.keys(config.sections).filter(k => k.startsWith("table_copy_"))];
    return keys.filter(key => {
      const sec = config.sections[key];
      return sec && sec.enabled !== false;
    });
  };

  const tableKeys = getTableKeys();

  const renderTableHTML = (tableKey: string, pageRows: any[], isLastPage: boolean) => {
    const tableConfig = config?.sections?.[tableKey];
    if (!tableConfig || tableConfig.enabled === false) return "";

    const activeColsForTable: string[] = tableConfig.columns || ["account_number", "account_name", "amount"];
    const tableTop = cm(tableConfig.top || "7.5cm");
    const tableLeft = cm(tableConfig.left ?? config?.layout?.left_margin ?? "2cm");
    const tableRight = cm(config?.layout?.right_margin ?? "2cm");
    const tableWidthNum = cmToNumber(tableConfig.width, Number.NaN);
    const hasExplicitTableWidth = Number.isFinite(tableWidthNum) && tableWidthNum > 0;
    const tableWidth = hasExplicitTableWidth
      ? cm(tableWidthNum)
      : `calc(100% - ${tableLeft} - ${tableRight})`;

    const thisRowHeight = tableConfig.row_height || config?.sections?.table?.row_height || "0.8cm";

    const getColWidth = (colId: string) => {
      const customWidth = tableConfig[`${colId}_width`];
      if (customWidth) return cm(customWidth);
      
      const fallback = colDefinitions[colId]?.defaultWidth || "30%";
      if (hasExplicitTableWidth) {
        if (fallback.endsWith("%")) {
          const pct = parseFloat(fallback) / 100;
          return cm(tableWidthNum * pct);
        }
        return cm(fallback);
      }
      return fallback;
    };

    const getColStyles = (colId: string) => {
      const defAlign = colDefinitions[colId]?.defaultAlign || "left";
      const align = tableConfig[`${colId}_align`] || defAlign;
      const fontSize = formatFontSize(tableConfig[`${colId}_font_size`] || "12px");
      const isBold = tableConfig[`${colId}_bold`] === true || tableConfig[`${colId}_bold`] === "true";
      const isItalic = tableConfig[`${colId}_italic`] === true || tableConfig[`${colId}_italic`] === "true";
      
      return {
        align,
        fontSize,
        fontWeight: isBold ? "bold" : "normal",
        fontStyle: isItalic ? "italic" : "normal",
      };
    };

    return `
      <table style="
        position: absolute;
        top: ${tableTop};
        left: ${tableLeft};
        border-collapse: collapse;
        width: ${tableWidth};
        table-layout: fixed;
        font-size: 12px;
      ">
        <colgroup>
          ${activeColsForTable.map(colId => `<col style="width: ${getColWidth(colId)};" />`).join("")}
        </colgroup>
        ${tableConfig.hide_header !== true ? `
        <thead>
          <tr style="border-bottom: 1px solid #000; font-weight: bold;">
            ${activeColsForTable.map(colId => {
              const styles = getColStyles(colId);
              const label = tableConfig[`${colId}_label`] || colDefinitions[colId]?.label || colId;
              const justifyMap: Record<string, string> = {
                left: "flex-start",
                right: "flex-end",
                center: "center",
              };
              const justify = justifyMap[styles.align] || "flex-start";
              return `
                <th style="
                  font-size: ${styles.fontSize};
                  font-weight: bold;
                  border-bottom: 1px solid #000;
                  padding: 0;
                ">
                  <div style="
                    display: flex;
                    align-items: center;
                    justify-content: ${justify};
                    height: ${cm(thisRowHeight)};
                    box-sizing: border-box;
                    padding: 2px 4px;
                  ">
                    ${escapeHtml(label)}
                  </div>
                </th>
              `;
            }).join("")}
          </tr>
        </thead>
        ` : ""}
        <tbody>
          ${pageRows
            .map(
              (row: any) => `
                <tr>
                  ${activeColsForTable.map(colId => {
                    const styles = getColStyles(colId);
                    let cellVal = row[colId];
                    if (["amount", "balance", "debit", "credit", "unit_price"].includes(colId)) {
                      const numVal = Number(cellVal);
                      cellVal = !isNaN(numVal) ? formatCurrency(numVal) : "";
                    }
                    if (config?.hide_na && typeof cellVal === 'string' && cellVal.trim().toUpperCase() === 'N/A') {
                      cellVal = "";
                    }
                    const justifyMap: Record<string, string> = {
                      left: "flex-start",
                      right: "flex-end",
                      center: "center",
                    };
                    const justify = justifyMap[styles.align] || "flex-start";
                    return `
                      <td style="
                        font-size: ${styles.fontSize};
                        font-weight: ${styles.fontWeight};
                        font-style: ${styles.fontStyle};
                        white-space: normal;
                        overflow-wrap: anywhere;
                        word-break: break-word;
                        padding: 0;
                      ">
                        <div style="
                          display: flex;
                          align-items: center;
                          justify-content: ${justify};
                          height: ${cm(thisRowHeight)};
                          box-sizing: border-box;
                          padding: 2px 4px;
                        ">
                          ${escapeHtml(cellVal)}
                        </div>
                      </td>
                    `;
                  }).join("")}
                </tr>
              `
            )
            .join("")}
        </tbody>
        ${isLastPage && tableConfig.hide_footer !== true ? `
          <tfoot>
            <tr style="border-top: 1px solid #000; font-weight: bold;">
              ${activeColsForTable.map((colId, idx) => {
                const styles = getColStyles(colId);
                let val = "";
                if (colId === "amount") {
                  val = formatCurrency(totalAmount);
                } else if (colId === "debit") {
                  const totalDebit = rows.reduce((sum: number, r: any) => sum + Number(r.debit || 0), 0);
                  val = totalDebit > 0 ? formatCurrency(totalDebit) : "";
                } else if (colId === "credit") {
                  const totalCredit = rows.reduce((sum: number, r: any) => sum + Number(r.credit || 0), 0);
                  val = totalCredit > 0 ? formatCurrency(totalCredit) : "";
                } else if (idx === 0) {
                  val = "Total:";
                }
                const isDoubleLine = tableConfig.double_line_footer === true || tableConfig.double_line_footer === "true";
                const borderBottomStyle = isDoubleLine ? "border-bottom: 3px double #000;" : "";
                const justifyMap: Record<string, string> = {
                  left: "flex-start",
                  right: "flex-end",
                  center: "center",
                };
                const justify = justifyMap[styles.align] || "flex-start";
                return `
                  <td style="
                    font-size: ${styles.fontSize};
                    font-weight: bold;
                    border-top: 1px solid #000;
                    ${borderBottomStyle}
                    padding: 0;
                  ">
                    <div style="
                      display: flex;
                      align-items: center;
                      justify-content: ${justify};
                      height: ${cm(thisRowHeight)};
                      box-sizing: border-box;
                      padding: 4px 4px ${isDoubleLine ? "2px" : "0px"} 4px;
                    ">
                      ${escapeHtml(val)}
                    </div>
                  </td>
                `;
              }).join("")}
            </tr>
          </tfoot>
        ` : ""}
      </table>
    `;
  };

  return pagedRows
    .map((pageRows, pageIndex) => {
      const isLastPage = pageIndex === pagedRows.length - 1;

      return `
        <div style="
          position: relative;
          width: ${pageWidth};
          height: ${pageHeight};
          font-family: 'Noto Serif', 'Noto Serif Sinhala', 'Noto Sans Tamil', 'Times New Roman', 'Iskoola Pota', 'Latha', serif;
          ${isLastPage ? "" : "page-break-after: always;"}
        ">
          ${allKeys.map((key) => renderField(key)).join("")}
          ${tableKeys.map(tableKey => renderTableHTML(tableKey, pageRows, isLastPage)).join("")}
        </div>
      `;
    })
    .join("");
};
